import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Container, Row, Col, Card, CardBody, CardTitle, Button, Alert } from 'reactstrap';
import { CreditCard } from 'lucide-react';
import Footer from '../../components/usuarios/Footer';
import Loader from '../../layouts/loader/Loader';
import redsysApiService from '../../services/http/redsysApiService';
import logoCedesca from '../../assets/images/logos/logo-cedesca.svg';
import logoCntec from '../../assets/images/logos/logo-cntec-0.svg';

const SEDES = {
  cedesca: {
    nombre: 'CEDESCA',
    razonSocial: "CENTRE D'ESTUDIS CATALUNYA",
    logo: logoCedesca,
    headquarter: 'CEDESCA',
  },
  cntec: {
    nombre: 'CNTEC',
    razonSocial: 'CNTEC EDUCATION',
    logo: logoCntec,
    headquarter: 'CNTEC',
  },
};

const extraerErrorMensaje = (err) => {
  const status = err?.response?.status;
  const body = err?.response?.data;
  if (body?.error) return { status, mensaje: body.error };
  if (status === 404) return { status, mensaje: 'Solicitud de cambio de tarjeta no encontrada.' };
  if (status === 409)
    return { status, mensaje: 'Este enlace de cambio de tarjeta ya se ha usado o ha caducado.' };
  return { status, mensaje: 'No se pudo cargar la solicitud de cambio de tarjeta.' };
};

const submitFormularioRedsys = ({ url, fields }) => {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = url;
  Object.entries(fields).forEach(([name, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
};

const CambioTarjeta = ({ sede }) => {
  const sedeInfo = SEDES[sede];
  const [searchParams] = useSearchParams();
  const uid = searchParams.get('uid');

  const [info, setInfo] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!uid) {
      setError({ mensaje: 'Falta el identificador de la solicitud en el enlace.' });
      setCargando(false);
      return undefined;
    }
    let cancelado = false;

    setCargando(true);
    setError(null);

    redsysApiService
      .cargarInfoCambioTarjeta({ uid, headquarter: sedeInfo.headquarter })
      .then((data) => {
        if (!cancelado) setInfo(data);
      })
      .catch((err) => {
        if (!cancelado) setError(extraerErrorMensaje(err));
      })
      .finally(() => {
        if (!cancelado) setCargando(false);
      });

    return () => {
      cancelado = true;
    };
  }, [uid, sedeInfo.headquarter]);

  const handleCambiar = async () => {
    setEnviando(true);
    setError(null);
    try {
      const respuesta = await redsysApiService.iniciarCambioTarjeta({
        uid,
        headquarter: sedeInfo.headquarter,
      });
      submitFormularioRedsys(respuesta);
    } catch (err) {
      setError(extraerErrorMensaje(err));
      setEnviando(false);
    }
  };

  return (
    <>
      <main className="bg-light min-vh-100 py-4">
        <Container>
          <Row className="justify-content-center mb-4">
            <Col xs="12" className="text-center">
              <img
                src={sedeInfo.logo}
                alt={sedeInfo.nombre}
                className="d-block mx-auto"
                style={{ height: '120px', maxWidth: '100%', width: 'auto' }}
              />
              <h2 className="mt-3 mb-1">Cambio de tarjeta</h2>
              <p className="text-muted">
                Actualice de forma segura la tarjeta asociada a sus pagos con Cyberpac.
              </p>
            </Col>
          </Row>

          <Row className="justify-content-center">
            <Col lg="8">
              {cargando && (
                <Card className="shadow-sm mb-4">
                  <CardBody className="p-4">
                    <Loader />
                  </CardBody>
                </Card>
              )}

              {!cargando && error && (
                <Alert color="danger" className="mb-4">
                  {error.mensaje}
                </Alert>
              )}

              {!cargando && !error && info && (
                <>
                  <Card className="shadow-sm mb-4">
                    <CardTitle tag="h5" className="border-bottom px-4 py-3 mb-0">
                      Datos del alumno
                    </CardTitle>
                    <CardBody className="p-4">
                      <div className="text-muted">
                        <strong>Alumno:</strong> {info.nombreAlumno}
                      </div>
                    </CardBody>
                  </Card>

                  <div className="text-center mb-5">
                    <Button
                      color="primary"
                      size="lg"
                      disabled={enviando}
                      onClick={handleCambiar}
                      className="px-5 py-3 d-inline-flex align-items-center gap-2"
                    >
                      <CreditCard size={20} />
                      {enviando ? 'Redirigiendo a Cyberpac…' : 'Actualizar tarjeta'}
                    </Button>
                    <p className="text-muted small mt-2 mb-0">
                      Será redirigido al entorno seguro de Cyberpac. No se realizará ningún cargo:
                      se trata de una operación de verificación de 0&nbsp;€.
                    </p>
                    <p className="text-muted small mt-2 mb-0">
                      Al continuar, autoriza a <strong>{sedeInfo.razonSocial}</strong> a utilizar la
                      nueva tarjeta para sus pagos conforme a las condiciones estipuladas.
                    </p>
                  </div>
                </>
              )}
            </Col>
          </Row>
        </Container>
      </main>

      <Footer />
    </>
  );
};

CambioTarjeta.propTypes = {
  sede: PropTypes.oneOf(Object.keys(SEDES)).isRequired,
};

export default CambioTarjeta;
